import { defineStore } from 'pinia'
import { apiService } from '@/services/api.js'
import { setupService } from '@/services/setup.js'
import echoService from '@/services/echo.js'
import { useMatchInviteStore } from '@/stores/matchInvite.js'
import { useMatchStore } from '@/stores/match.js'
import { useToast } from '@/composables/useToast.js'
import { useTranslationsStore } from '@/stores/translations.js'
import { normalizeNotification } from '@/utils/notificationHelpers.js'
import { matchTypeName } from '@/constants/matchTypes.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import analytics from '@/services/analytics.ts'
import * as Sentry from '@sentry/vue'

// A dart setup is five optional components; how many are filled in says how far people get with
// the builder, which is what setup_created / setup_edited are for.
const SETUP_COMPONENT_KEYS = ['dart_id', 'tip_id', 'barrel_id', 'shaft_id', 'flight_id']

function countSetupComponents(setupData) {
    return SETUP_COMPONENT_KEYS.filter((key) => setupData?.[key]).length
}

// Helper function to navigate without direct router dependency
const navigateTo = (route) => {
    // Use window.location for hard navigation to break circular dependency
    window.location.hash = `#${route}`
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: null,
        user: null,
        dart_setups: null,
        active_matches: [],
        recent_matches: [],
        profile: [],
        sent_requests: [],
        received_requests: [],
        friends: [],
        statistics: [],
        matchtypes: [],
        dartbots: [],
        userChannel: null,
        inbox_notifications: [],
        blocked_users: [],
        blockedUsersLoaded: false,
        needs_onboarding: false,
        maintenanceMode: false,
        // Bookkeeping only, never persisted - lets verifyToken() detect and
        // discard a stale response that resolves after a newer call already
        // updated the store (see verifyToken for why this matters).
        _verifyTokenRequestId: 0,
    }),

    persist: {
        key: 'dartshopper-auth',
        storage: localStorage,
        paths: ['token', 'user'],
        omit: ['maintenanceMode'],
    },

    getters: {
        isAuthenticated: (state) => {
            return !!(state.token && state.user)
        },

        requiresOnboarding: (state) => {
            return !!(state.token && state.user) && state.needs_onboarding
        },

        todayNotifications: (state) => {
            return state.inbox_notifications.filter((notification) => notification.is_today)
        },

        olderNotifications: (state) => {
            return state.inbox_notifications.filter((notification) => !notification.is_today)
        },

        unreadNotifications: (state) => {
            return state.inbox_notifications.filter((notification) => !notification.read_at)
        },

        readNotifications: (state) => {
            return state.inbox_notifications.filter((notification) => notification.read_at)
        },

        totalUnreadCount: (state) => {
            return state.inbox_notifications.filter((notification) => !notification.read_at).length
        },

        todayUnreadCount: (state) => {
            return state.inbox_notifications.filter(
                (notification) => notification.is_today === 1 && !notification.read_at,
            ).length
        },
    },

    actions: {
        async loginWithEmailPassword(email, password) {
            try {
                const response = await apiService.post('/api/v1/temp/graphql-login', {
                    email,
                    password,
                })

                if (response.success && response.data && response.data.accessToken) {
                    return await this.authenticateExternal(response.data.accessToken)
                } else {
                    console.warn('Login successful but no accessToken returned')
                    this.clearAuth()
                    return false
                }
            } catch (error) {
                console.error('Email/password login error:', error)
                this.clearAuth()
                return false
            }
        },

        async setToken(bridgeToken) {
            this.token = bridgeToken
            if (bridgeToken) {
                return await this.authenticateExternal(bridgeToken)
            } else {
                this.clearAuth()
                return false
            }
        },

        async authenticateExternal(bridgeToken) {
            try {
                apiService.setToken(bridgeToken)
                const data = await apiService.post('/api/v1/login/external', {})

                if (data.token) {
                    this.token = data.token
                    return await this.verifyToken()
                } else {
                    console.warn('Authentication successful but no token returned')
                    this.clearAuth()
                    return false
                }
            } catch (error) {
                console.error('Token exchange error:', error)
                this.clearAuth()
                return false
            }
        },

        async verifyToken() {
            if (!this.token) {
                this.clearAuth()
                return false
            }

            const requestId = ++this._verifyTokenRequestId

            try {
                apiService.setToken(this.token)
                const data = await apiService.get('/api/v1/user')

                // A newer verifyToken() call was started after this one and
                // has already applied its (fresher) response - applying this
                // stale response now would silently revert the store.
                if (requestId !== this._verifyTokenRequestId) {
                    return true
                }

                if (data.maintenance_mode === true) {
                    console.log('App is in maintenance mode')
                    this.maintenanceMode = true
                    return false
                }

                this.user = data.user || data
                this.active_matches = data.active_matches || []
                this.recent_matches = data.recent_matches || []
                this.dart_setups = data.dart_setups || []

                if (this.user) {
                    Sentry.setUser({
                        id: String(this.user.id),
                        email: this.user.email,
                        username: this.user.name || this.user.email,
                    })
                }
                this.profile = data.profile || []
                this.matchtypes = data.matchtypes || []
                this.dartbots = data.dartbots || []
                this.sent_requests = data.sent_requests || []
                this.received_requests = data.received_requests || []
                this.friends = data.friends || []
                this.statistics = data.statistics || []
                this.inbox_notifications = data.notifications || []
                this.needs_onboarding = !this.user.onboarded_at

                echoService.init(() => this.token)

                if (!this.userChannel) {
                    this.setupUserWebsocketListeners()
                }

                return true
            } catch (error) {
                console.error('Token verification error:', error)

                // Only clear auth if we get a 401 Unauthorized response
                // This means the token is actually invalid
                if (error.status === 401) {
                    console.log('Token is invalid (401), logging out')
                    this.clearAuth()
                    return false
                }

                // For other errors (network issues, 500s, etc.), don't log out
                // Just return false to indicate verification failed
                console.warn('Token verification failed but not clearing auth (non-401 error)')
                return false
            }
        },

        async searchProfiles(query) {
            if (!query || !query.trim()) {
                return []
            }

            try {
                const response = await apiService.get(
                    `/api/v1/profile/search?q=${encodeURIComponent(query.trim())}`,
                )
                const profiles = response.profiles || response || []

                // The query is someone's name or tag - free text about a real person, so only
                // its length goes out. A search with no results is the signal worth having.
                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_SEARCHED, {
                    query_len: query.trim().length,
                    result_count: Array.isArray(profiles) ? profiles.length : 0,
                })

                return profiles
            } catch (error) {
                console.error('Failed to search profiles:', error)
                throw error
            }
        },

        async sendFriendRequest(profileId, inviteCode = null) {
            try {
                const data = {
                    profile_id_friend: profileId,
                    ...(inviteCode && { invitecode_used: inviteCode }),
                }

                const response = await apiService.post('/api/v1/profile/friends', data)

                this.sent_requests = response.sent_requests || []
                this.received_requests = response.received_requests || []
                this.friends = response.friends || []

                // Backend auto-unblocks this profile if we had it blocked. Keep
                // local state in sync so the Unblock affordance disappears.
                this.blocked_users = this.blocked_users.filter(
                    (b) => b.blocked_profile_id !== profileId,
                )

                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_SENT, {
                    via_invite_code: !!inviteCode,
                    friend_count: this.friends.length,
                })

                return response
            } catch (error) {
                console.error('Failed to send friend request:', error)
                throw error
            }
        },

        async acceptFriendRequest(friendshipId) {
            try {
                const response = await apiService.patch(
                    `/api/v1/profile/friends/${friendshipId}/accept`,
                    {},
                )
                this.sent_requests = response.sent_requests || []
                this.received_requests = response.received_requests || []
                this.friends = response.friends || []

                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_ACCEPTED, {
                    friend_count: this.friends.length,
                })

                return response
            } catch (error) {
                console.error('Failed to accept friend request:', error)
                throw error
            }
        },

        async declineFriendRequest(friendshipId) {
            try {
                const response = await apiService.delete(
                    `/api/v1/profile/friends/${friendshipId}/decline`,
                )
                this.sent_requests = response.sent_requests || []
                this.received_requests = response.received_requests || []
                this.friends = response.friends || []

                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_DECLINED, {
                    friend_count: this.friends.length,
                })

                return response
            } catch (error) {
                console.error('Failed to decline friend request:', error)
                throw error
            }
        },

        async cancelFriendRequest(friendshipId) {
            try {
                const response = await apiService.delete(
                    `/api/v1/profile/friends/${friendshipId}`,
                )
                this.sent_requests = response.sent_requests || []
                this.received_requests = response.received_requests || []
                this.friends = response.friends || []

                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_CANCELLED, {
                    friend_count: this.friends.length,
                })

                return response
            } catch (error) {
                console.error('Failed to cancel friend request:', error)
                throw error
            }
        },

        async fetchBlockedUsers() {
            try {
                const response = await apiService.get('/api/v1/profile/blocks')
                this.blocked_users = Array.isArray(response) ? response : response?.data || []
                this.blockedUsersLoaded = true
                return this.blocked_users
            } catch (error) {
                console.error('Failed to fetch blocked users:', error)
                throw error
            }
        },

        async blockProfile(profileId) {
            try {
                const response = await apiService.post('/api/v1/profile/blocks', {
                    profile_id: profileId,
                })
                this.blocked_users = Array.isArray(response) ? response : response?.data || []
                this.blockedUsersLoaded = true
                return response
            } catch (error) {
                console.error('Failed to block profile:', error)
                throw error
            }
        },

        async unblockProfile(profileId) {
            if (!this.blockedUsersLoaded) {
                await this.fetchBlockedUsers()
            }

            const blocks = this.blocked_users.filter((b) => b.blocked_profile_id === profileId)
            if (!blocks.length) {
                throw new Error('Block not found for this profile')
            }

            try {
                // Delete every matching block, not just the first - the account
                // should never have more than one, but if it does (e.g. a stale
                // duplicate), leaving one behind keeps the profile blocked.
                await Promise.all(
                    blocks.map((block) => apiService.delete(`/api/v1/profile/blocks/${block.id}`)),
                )
                const blockIds = new Set(blocks.map((b) => b.id))
                this.blocked_users = this.blocked_users.filter((b) => !blockIds.has(b.id))
            } catch (error) {
                console.error('Failed to unblock profile:', error)
                throw error
            }
        },

        async removeFriend(friendshipId) {
            try {
                const response = await apiService.delete(`/api/v1/profile/friends/${friendshipId}`)
                this.sent_requests = response.sent_requests || []
                this.received_requests = response.received_requests || []
                this.friends = response.friends || []

                analytics.trackEvent(ANALYTICS_EVENTS.FRIEND_REMOVED, {
                    friend_count: this.friends.length,
                })

                return response
            } catch (error) {
                console.error('Failed to remove friend:', error)
                throw error
            }
        },

        setupUserWebsocketListeners() {
            if (!this.user?.id || !echoService.instance) {
                return
            }

            this.cleanupUserWebsocketListeners()

            try {
                const channelName = `App.Models.User.${this.user.id}`
                this.userChannel = echoService.instance
                    .private(channelName)
                    .listen('.friends.updated', (e) => {
                        console.log('Friends updated:', e)
                        this.sent_requests = e.sent_requests || []
                        this.received_requests = e.received_requests || []
                        this.friends = e.friends || []
                    })
                    .listen('.inbox.updated', (e) => {
                        console.log('Inbox updated:', e)
                        if (e.notifications) {
                            const incoming = e.notifications.map(normalizeNotification)
                            const incomingById = new Map(incoming.map((n) => [n.id, n]))
                            const existingIds = new Set(
                                this.inbox_notifications.map((n) => n.id),
                            )

                            const updatedExisting = this.inbox_notifications.map(
                                (n) => incomingById.get(n.id) ?? n,
                            )
                            const newOnes = incoming.filter((n) => !existingIds.has(n.id))

                            this.inbox_notifications = [...newOnes, ...updatedExisting]
                        }
                    })
                    .listen('.user.data.updated', (e) => {
                        console.log('User data updated:', e)
                        if (e.profile) {
                            this.profile = e.profile
                        }
                        if (e.sent_requests) {
                            this.sent_requests = e.sent_requests
                        }
                        if (e.received_requests) {
                            this.received_requests = e.received_requests
                        }
                        if (e.friends) {
                            this.friends = e.friends
                        }
                        if (e.notifications) {
                            this.inbox_notifications = e.notifications
                        }
                        if (e.statistics) {
                            this.statistics = e.statistics
                        }
                        if (e.recent_matches) {
                            this.recent_matches = e.recent_matches
                        }
                        if (e.active_matches) {
                            this.active_matches = e.active_matches
                            this.reconcileLiveMatch()
                        }
                    })
                    .notification((notification) => {
                        console.log('Notification received:', notification)
                        this.handleNotification(notification)
                    })

                if (window.__memoryLeakDetector) {
                    window.__memoryLeakDetector.trackEchoChannel(channelName)
                }
            } catch (error) {
                console.error('Error setting up user websocket listeners:', error)
            }
        },

        reconcileLiveMatch() {
            // A fresh user-data broadcast carries the authoritative list of
            // active matches. If the match we are actively playing is no longer
            // in it (a player left, or it ended) and the game isn't already
            // over on our side, end it locally. This recovers the in-game view
            // even when the per-match `.match.ended` broadcast was missed, e.g.
            // when the socket dropped in the webview.
            const matchStore = useMatchStore()
            const currentMatchId = matchStore.match?.id

            if (!currentMatchId || matchStore.isMatchOver) {
                return
            }

            const stillActive = (this.active_matches || []).some(
                (match) => String(match.id) === String(currentMatchId),
            )

            if (!stillActive) {
                matchStore.handleMatchEnded({
                    match_id: currentMatchId,
                    reason: 'player_left',
                })
            }
        },

        cleanupUserWebsocketListeners() {
            if (this.userChannel && this.user?.id) {
                try {
                    this.userChannel.stopListening('.friends.updated')
                    this.userChannel.stopListening('.inbox.updated')
                    this.userChannel.stopListening('.user.data.updated')
                    if (echoService.instance) {
                        const channelName = `App.Models.User.${this.user.id}`
                        echoService.instance.leave(channelName)

                        if (window.__memoryLeakDetector) {
                            window.__memoryLeakDetector.untrackEchoChannel(channelName)
                        }
                    }
                } catch (error) {
                    console.error('Error cleaning up user websocket listeners:', error)
                }
                this.userChannel = null
            }
        },

        /**
         * Re-fetch the accepted friends list so each friend's `last_seen_at`
         * (and therefore their online/offline state) stays current. Used for
         * lightweight polling while the friends list is on screen.
         */
        async refreshFriends() {
            try {
                const response = await apiService.get('/api/v1/profile/friends')
                this.friends = Array.isArray(response) ? response : response?.data || []
                return this.friends
            } catch (error) {
                console.error('Failed to refresh friends:', error)
                throw error
            }
        },

        async handleNotification(notification) {
            try {
                const { addToast } = useToast()
                const { t } = useTranslationsStore()

                const normalizedNotification = normalizeNotification(notification)

                if (!this.inbox_notifications.find((n) => n.id === normalizedNotification.id)) {
                    this.inbox_notifications.unshift(normalizedNotification)
                }

                if (notification.type === 'App\\Notifications\\InviteToMatchNotification') {
                    const fromUser =
                        normalizedNotification.data?.from_user?.tag_name ||
                        normalizedNotification.data?.from_user?.name ||
                        t('play.a_player')
                    const matchType = matchTypeName(
                        normalizedNotification.data?.match?.matchtype_id,
                        normalizedNotification.data?.match?.matchtype_name || null,
                    )
                    const message = matchType
                        ? t('notifications.invite_body', { from: fromUser, matchType })
                        : t('notifications.invite_body_generic', { from: fromUser })
                    const avatar = normalizedNotification.data?.from_user?.avatar || null

                    addToast(t('notifications.invite_title'), 5000, message, avatar, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }

                if (notification.type === 'App\\Notifications\\MatchInviteAcceptedNotification') {
                    const fromUser =
                        normalizedNotification.data?.from_user?.tag_name ||
                        normalizedNotification.data?.from_user?.name ||
                        t('play.a_player')
                    const matchType = matchTypeName(
                        normalizedNotification.data?.match?.matchtype_id,
                        normalizedNotification.data?.match?.matchtype_name || null,
                    )
                    const message = matchType
                        ? t('notifications.invite_accepted_body', { from: fromUser, matchType })
                        : t('notifications.match_accepted_body_generic', { name: fromUser })
                    const avatar = normalizedNotification.data?.from_user?.avatar || null

                    addToast(t('notifications.invite_accepted_title'), 5000, message, avatar)
                }

                if (notification.type === 'App\\Notifications\\MatchInviteDeclinedNotification') {
                    const fromUser =
                        normalizedNotification.data?.from_user?.tag_name ||
                        normalizedNotification.data?.from_user?.name ||
                        t('play.a_player')
                    const matchType = matchTypeName(
                        normalizedNotification.data?.match?.matchtype_id,
                        normalizedNotification.data?.match?.matchtype_name || null,
                    )
                    const message = matchType
                        ? t('notifications.invite_declined_body', { from: fromUser, matchType })
                        : t('notifications.match_declined_body_generic', { name: fromUser })
                    const avatar = normalizedNotification.data?.from_user?.avatar || null

                    addToast(t('notifications.invite_declined_title'), 5000, message, avatar)
                }

                if (notification.type === 'App\\Notifications\\RemovedFromMatchNotification') {
                    this.handleRemovedFromMatch(notification)
                }

                if (notification.type === 'App\\Notifications\\FriendRequestReceivedNotification') {
                    addToast(t('notifications.friend_request_received'), 5000, notification.message, null, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }

                if (notification.type === 'App\\Notifications\\FriendRequestAcceptedNotification') {
                    addToast(t('notifications.friend_request_accepted'), 5000, notification.message, null, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }

                if (notification.type === 'App\\Notifications\\FriendRequestDeclinedNotification') {
                    addToast(t('notifications.friend_request_declined'), 5000, notification.message, null, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }

                if (notification.type === 'App\\Notifications\\FriendshipRemovedNotification') {
                    addToast(t('notifications.friendship_removed'), 5000, notification.message, null, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }

                if (
                    notification.type ===
                    'App\\Notifications\\FriendshipCancelledRequestNotification'
                ) {
                    addToast(t('friends.request_cancelled_title'), 5000, notification.message, null, {
                        label: t('notifications.view'),
                        action: () => navigateTo('/inbox'),
                    })
                }
            } catch (error) {
                console.error('Error handling notification:', error)
            }
        },

        markNotificationAsRead(notificationId) {
            const notification = this.inbox_notifications.find((n) => n.id === notificationId)
            if (notification) {
                notification.read_at = new Date().toISOString()
                console.log('Notification marked as read:', notificationId)
            }
        },

        markAllInboxNotificationsAsRead() {
            const now = new Date().toISOString()
            this.inbox_notifications.forEach((notification) => {
                if (!notification.read_at) {
                    notification.read_at = now
                }
            })
            console.log('All notifications marked as read')
        },

        // Removed handleMatchInvite as we now handle invites through the inbox

        async handleRemovedFromMatch(notification) {
            const matchInviteStore = useMatchInviteStore()
            const { addToast } = useToast()
            const { t } = useTranslationsStore()

            const matchId = notification.match_id
            const hadAccepted = notification.had_accepted

            if (hadAccepted) {
                addToast(t('notifications.removed_title'), 5000, notification.message)
            } else {
                matchInviteStore.closeInviteIfMatch(matchId)
            }
        },

        logout() {
            this.clearAuth()
        },

        async updateUser(userData) {
            try {
                const formData = new FormData()
                formData.append('_method', 'PUT')

                Object.keys(userData).forEach((key) => {
                    if (userData[key] !== null && userData[key] !== undefined) {
                        formData.append(key, userData[key])
                    }
                })

                const response = await fetch(`${apiService.baseURL}/api/v1/user`, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${this.token}`,
                    },
                    body: formData,
                })

                const data = await response.json()

                if (!response.ok) {
                    const error = new Error(`Update user failed: ${response.status}`)
                    error.response = { data, status: response.status }
                    throw error
                }

                if (this.user) {
                    this.user.onboarded_at = data.user.onboarded_at
                    this.profile.tag_name = data.profile.tag_name
                    if (data.profile.avatar) {
                        this.profile.avatar = data.profile.avatar
                    }
                    this.needs_onboarding = false
                }

                return data
            } catch (error) {
                console.error('Failed to update user:', error)
                throw error
            }
        },

        completeOnboarding(userData) {
            if (this.user) {
                this.user.onboarded_at = userData.onboarded_at || new Date().toISOString()
                this.user.tag_name = userData.tag_name
                if (userData.avatar_url) {
                    this.user.avatar_url = userData.avatar_url
                }
                this.needs_onboarding = false
            }
        },

        clearAuth() {
            this.cleanupUserWebsocketListeners()

            Sentry.setUser(null)

            this.token = null
            this.user = null
            this.profile = []
            this.matchtypes = []
            this.dartbots = []
            this.sent_requests = []
            this.received_requests = []
            this.friends = []
            this.statistics = []
            this.blocked_users = []
            this.blockedUsersLoaded = false
            this.needs_onboarding = false
            this.maintenanceMode = false

            echoService.disconnect()
        },

        async initializeFromStorage() {
            if (this.token) {
                return await this.verifyToken()
            }
            return false
        },

        async fetchSetups() {
            try {
                const response = await setupService.fetchSetups()
                if (response.dart_setups) {
                    this.dart_setups = response.dart_setups
                }
                return response
            } catch (error) {
                console.error('Failed to fetch setups:', error)
                throw error
            }
        },

        async createSetup(setupData) {
            try {
                const response = await setupService.createSetup(setupData)
                if (response.dart_setup) {
                    await this.fetchSetups()
                }

                analytics.trackEvent(ANALYTICS_EVENTS.SETUP_CREATED, {
                    setup_id: String(response.dart_setup?.id ?? ''),
                    component_count: countSetupComponents(setupData),
                    is_default: !!setupData?.is_default,
                })
                return response
            } catch (error) {
                console.error('Failed to create setup:', error)
                throw error
            }
        },

        async updateSetup(setupId, setupData) {
            try {
                const response = await setupService.updateSetup(setupId, setupData)
                if (response.dart_setup) {
                    await this.fetchSetups()
                }

                analytics.trackEvent(ANALYTICS_EVENTS.SETUP_EDITED, {
                    setup_id: String(setupId),
                    component_count: countSetupComponents(setupData),
                    is_default: !!setupData?.is_default,
                })
                return response
            } catch (error) {
                console.error('Failed to update setup:', error)
                throw error
            }
        },

        async deleteSetup(setupId) {
            try {
                await setupService.deleteSetup(setupId)
                await this.fetchSetups()

                analytics.trackEvent(ANALYTICS_EVENTS.SETUP_DELETED, {
                    setup_id: String(setupId),
                })
            } catch (error) {
                console.error('Failed to delete setup:', error)
                throw error
            }
        },
    },
})
